import { cloneDeep, isEqual } from 'lodash';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import DuoApi from 'client/MFA/duo/DuoApi';
import DuoMessages from 'client/MFA/duo/DuoMessages.json';
import Vue from 'vue';

const ADD_ACCOUNT = 'ADD_ACCOUNT';
const ADD_APPLICATION = 'ADD_APPLICATION';
const DELETE_DUO_APPLICATION = 'DELETE_DUO_APPLICATION';
const SET_STATE = 'SET_STATE';
const SET_ACCOUNTS = 'SET_ACCOUNTS';
const SET_APPLICATION_ERROR = 'SET_APPLICATION_ERROR';
const SET_APPLICATIONS = 'SET_APPLICATIONS';
const SET_DUO_APPLICATION = 'SET_DUO_APPLICATION';
const SET_STARTING_STATE = 'SET_STARTING_STATE';
const SET_USER_PORTAL_ERROR = 'SET_USER_PORTAL_ERROR';
const SET_USER_PORTAL_DUO_CONFIG = 'SET_USER_PORTAL_DUO_CONFIG';

const initialState = () => ({
  duoAccounts: [],
  duoApplications: [],
  userPortalDuoConfig: {
    enabled: false,
  },
});

export default {
  namespaced: true,

  state: {
    ...initialState(),
    startingState: { ...initialState() },
    errorSavingUserPortalConfig: false,
    errorSavingApplication: false,
  },

  mutations: {
    [ADD_ACCOUNT]: (state, account = {}) => {
      const accounts = [account];
      Vue.set(state, 'duoAccounts', cloneDeep(accounts));
    },
    [ADD_APPLICATION]: (state, application = {}) => {
      // This mutation will need to be smarter when there are multiple applications
      // Duo can protect. It will either need a "Pending state" or the Duo
      // applications array will need to be cleaned of "dirty" data and then data
      // from the API merged into state.
      const apps = [cloneDeep(application)];
      Vue.set(state, 'duoApplications', apps);
    },
    [SET_ACCOUNTS]: (state, accounts = []) => {
      Vue.set(state, 'duoAccounts', cloneDeep(accounts));
    },
    [SET_APPLICATIONS]: (state, applications = []) => {
      let apps = applications;

      if (state.startingState.duoApplications.length > 0
        && state.startingState.userPortalDuoConfig.enabled === true) {
        apps = state.startingState.duoApplications;
      }

      Vue.set(state, 'duoApplications', cloneDeep(apps));
    },
    [SET_APPLICATION_ERROR]: (state, hasError) => {
      Vue.set(state, 'errorSavingApplication', !!hasError);
    },
    [DELETE_DUO_APPLICATION]: (state, deletedApplication) => {
      const itemIndex = state.duoApplications.indexOf(deletedApplication);
      const arr = [...state.duoApplications];

      arr.splice(itemIndex, 1);

      Vue.set(state, 'duoApplications', cloneDeep(arr));
    },
    [SET_STATE]: (state) => {
      Vue.set(state, 'duoAccounts', cloneDeep(state.startingState.duoAccounts));
      Vue.set(state, 'duoApplications', cloneDeep(state.startingState.duoApplications));
      Vue.set(state, 'userPortalDuoConfig', cloneDeep(state.startingState.userPortalDuoConfig));
    },
    [SET_DUO_APPLICATION]: (state, change = {}) => {
      Vue.set(state, 'duoApplications', [{
        ...state.duoApplications[0],
        ...change,
      }]);
    },
    [SET_USER_PORTAL_DUO_CONFIG]: (state, config = {}) => {
      Vue.set(state, 'userPortalDuoConfig', cloneDeep(config));
    },
    [SET_USER_PORTAL_ERROR]: (state, hasError) => {
      Vue.set(state, 'errorSavingUserPortalConfig', !!hasError);
    },
    [SET_STARTING_STATE]: (state, type) => {
      if (!type) {
        Vue.set(state.startingState, 'duoAccounts', cloneDeep(state.duoAccounts));
        Vue.set(state.startingState, 'duoApplications', cloneDeep(state.duoApplications));
        Vue.set(state.startingState, 'userPortalDuoConfig', cloneDeep(state.userPortalDuoConfig));
        return;
      }

      Vue.set(state.startingState, type, cloneDeep(state[type]));
    },
  },

  getters: {
    disablingUserConfig(state) {
      return state.userPortalDuoConfig.enabled === false;
    },

    stateChange(_, getters) {
      const changes = (getters.stateChangeDuoConfig || getters.stateChangeApplications);
      return changes === true;
    },

    stateChangeDuoConfig(state = {}) {
      return state.userPortalDuoConfig.enabled !== state.startingState.userPortalDuoConfig.enabled;
    },

    stateChangeApplications(state = {}) {
      return !isEqual(state.duoApplications, state.startingState.duoApplications);
    },
    enabledInUserPortal(state = {}) {
      return state.userPortalDuoConfig.enabled;
    },
    hasChanges(_, getters) {
      return getters.stateChange;
    },
    hasErrors(state = {}) {
      return state.errorSavingApplication || state.errorSavingUserPortalConfig;
    },
  },

  actions: {
    async getDuoAccounts({ commit }) {
      try {
        const result = await DuoApi.getAccounts();
        commit(SET_ACCOUNTS, result);
        commit(SET_STARTING_STATE, 'DuoAccounts');
      } catch (err) {
        throw new Error(err || 'Could not get accounts');
      }
    },

    async createDuoAccount({ commit }) {
      try {
        const result = await DuoApi.createAccount();

        commit(SET_APPLICATION_ERROR, false);
        return commit(ADD_ACCOUNT, result);
      } catch (err) {
        commit(SET_APPLICATION_ERROR, true);
        throw new Error(err || 'Failed to create Duo Account');
      }
    },

    async getDuoApplications({ commit }, payload = { id: '' }) {
      const result = await DuoApi.getApplications(payload.id);
      commit(SET_APPLICATIONS, result);
      commit(SET_STARTING_STATE, 'duoApplications');
    },

    async createDuoApplication({ commit, state }) {
      const application = state.duoApplications[0];
      const accountId = state.duoAccounts[0] && state.duoAccounts[0].id;

      if (!application) throw new Error('No Duo Application configuration to create');
      if (!accountId) throw new Error('No Duo Account ID');

      try {
        const result = await DuoApi.createApplication(application, accountId);

        commit(SET_APPLICATION_ERROR, false);
        commit(ADD_APPLICATION, result);
        return commit(SET_STARTING_STATE, 'duoApplications');
      } catch (err) {
        commit(SET_APPLICATION_ERROR, true);
        return new Error(err || 'Could not create application');
      }
    },

    // TODO:
    // one more action: there are local application changes
    // but no application saved in the API. Delete the local application.
    // Would just be #commit("reset_state', duoApplications)

    async deleteDuoApplication({ commit, state }) {
      if (state.duoApplications.length === 0) {
        return undefined;
      }

      const applicationId = state.duoApplications[0] && state.duoApplications[0].id;
      const accountId = state.duoApplications[0] && state.duoAccounts[0].id;

      try {
        const deletedApplication = await DuoApi.deleteApplication(applicationId, accountId);

        commit(SET_APPLICATION_ERROR, false);
        commit(DELETE_DUO_APPLICATION, deletedApplication);
        return commit(SET_STARTING_STATE, 'duoApplications');
      } catch (error) {
        commit(SET_APPLICATION_ERROR, true);
        throw new Error(error || 'Could not delete Duo application');
      }
    },

    async saveDuoApplication({ commit, dispatch, state }) {
      const application = state.duoApplications[0];
      const accountId = state.duoAccounts[0] && state.duoAccounts[0].id;

      if (!application) throw new Error('No Duo Application configuration to save');
      if (!accountId) throw new Error('No Duo Account ID');

      try {
        const result = await DuoApi.saveApplication(application, accountId);

        commit(SET_APPLICATION_ERROR, false);
        commit(SET_DUO_APPLICATION, result);
        commit(SET_STARTING_STATE, 'duoApplications');
        dispatch(`${notificationModule}/triggerSuccessNotification`, {
          title: DuoMessages.ApplicationConfigSaved,
        }, { root: true });
        return;
      } catch (error) {
        commit(SET_APPLICATION_ERROR, true);
        throw new Error(error || DuoMessages.ApplicationSaveError);
      }
    },

    duoApplicationChange({ commit }, payload = {}) {
      commit(SET_DUO_APPLICATION, payload);
    },

    /**
     * When Duo is protecting multiple applications, the requests for
     * different Duo Configs will be more complicated. There will need
     * to be logic to discern which Duo Config is being requested and which
     * Duo Application that config is a child of.
     *
     * getting all MFA configs can be done with:
     * GET /api/userportal/mfa/
     */
    async getUserPortalDuoConfig({ commit }, payload = { id: '' }) {
      const response = await DuoApi.getUserPortalDuoConfig(payload.id);
      commit(SET_USER_PORTAL_DUO_CONFIG, response);
      commit(SET_STARTING_STATE, 'userPortalDuoConfig');
    },

    async saveUserPortalDuoConfig({ commit, dispatch, state }) {
      const applicationId = state.duoApplications[0] && state.duoApplications[0].id;
      const { enabled } = state.userPortalDuoConfig;

      if (enabled === false && !applicationId) {
        return undefined;
      }

      try {
        const result = await DuoApi.saveDuoConfig({
          ...state.userPortalDuoConfig,
          id: applicationId,
        });
        commit(SET_USER_PORTAL_ERROR, false);
        commit(SET_USER_PORTAL_DUO_CONFIG, result);
        commit(SET_STARTING_STATE);
        const statusMessage = state.userPortalDuoConfig.enabled
          ? DuoMessages.UserPortalEnabled : DuoMessages.UserPortalDisabled;
        dispatch(`${notificationModule}/triggerSuccessNotification`, {
          title: statusMessage,
        }, { root: true });
        return null;
      } catch (error) {
        commit(SET_USER_PORTAL_ERROR, true);
        throw Error(error || DuoMessages.ApplicationSaveError);
      }
    },

    async resetDuoState({ commit }) {
      commit(SET_STATE);
    },

    toggleDuoEnabled({ commit }, payload = {}) {
      commit(SET_USER_PORTAL_DUO_CONFIG, payload);
    },

    /**
     * The following requests are simplified by depending on there only being
     * one Account, one Application, and one Duo Config. This will change
     * sometime in the future when Duo MFA is used to authorize different
     * "applications". This possibility is hinted at by DuoAccounts and
     * DuoApplications being arrays.
     *
     * In Duo jargon User Portal is an application. A single RADIUS server
     * could be an application. A System Group could be an application.
     */
    async getAllDuoSettings({ dispatch, state }) {
      await dispatch('getDuoAccounts');

      if (!state.duoAccounts[0] || !state.duoAccounts[0].id) return;
      await dispatch('getDuoApplications', { id: state.duoAccounts[0].id });

      if (!state.duoApplications[0] || !state.duoApplications[0].id) return;
      await dispatch('getUserPortalDuoConfig', { id: state.duoApplications[0].id });
    },

    async saveDuoChanges({ dispatch, getters, state }) {
      try {
        // TODO: modify return value to an object:
        // {message: STRING} so the callback can decide to show a growl or do nothing.

        // User had a previously enabled UserPortal Duo Config and is disabling
        // UI must disable the config then delete the application.
        // The assumption is the API enforced and validated the requirements:
        // 1. There is a Duo Account
        // 2. There is a Duo Application
        // 3. There is a Duo Config
        if (state.userPortalDuoConfig.enabled === false) {
          await dispatch('saveUserPortalDuoConfig');
          await dispatch('deleteDuoApplication');

          return { message: 'The Duo application configuration has been disabled and removed.' };
        }

        // User is enabling, but has no Accounts. An Account needs to be created.
        if (state.duoAccounts.length === 0) {
          await dispatch('createDuoAccount');
        }

        // TODO: refactor with the following two commented out conditionals
        // so saving/modifying Applications is seperated from saving/modifying
        // Configs
        // wouldn't need to save them anywhere else now...
        // if (state.startingState.duoApplications.length === 0
        //   && getters.stateChangeApplications) {
        //   await dispatch('createDuoApplication');
        // }

        // if (state.startingState.duoApplications.length > 0
        //   && getters.stateChangeApplications) {
        //   await dispatch('saveDuoApplication');
        // }

        // User began with no Applications and has configured one.
        // We can assume that:
        // 1) They need to create an application
        // 2) They have toggled Duo Config to enabled -- otherwise they wouldn't see the form
        // Checking getters.stateChangeDuoConfig == true is being doubly safe.
        // They can't see the form without toggling to true.
        if (
          state.startingState.duoApplications.length === 0
          && getters.stateChangeApplications
          && getters.stateChangeDuoConfig
        ) {
          await dispatch('createDuoApplication');
          await dispatch('saveUserPortalDuoConfig');
          return { message: 'The Duo application configuration has been saved.' };
        }

        // User needs to create an application, but not changing config
        if (
          state.startingState.duoApplications.length === 0
          && getters.stateChangeApplications
        ) {
          await dispatch('createDuoApplication');
        }

        // User is modifying an existing application
        if (!getters.stateChangeDuoConfig && getters.stateChangeApplications) {
          await dispatch('saveDuoApplication');

          return { message: 'Updates to the Duo application configuration have been saved.' };
        }

        // user created an application and config via the API and is enabling it
        // via the UI
        if (state.userPortalDuoConfig.enabled === true
          && getters.stateChangeDuoConfig
          && !getters.stateChangeApplications
          && state.startingState.duoApplications.length > 0
        ) {
          await dispatch('saveUserPortalDuoConfig');

          return { message: 'The Duo application configuration has been enabled.' };
        }

        if (getters.stateChangeDuoConfig && !getters.stateChangeApplications) {
          await dispatch('saveUserPortalDuoConfig');

          return { message: 'The Duo application configuration has been enabled.' };
        }

        if (getters.stateChangeDuoConfig && getters.stateChangeApplications) {
          await dispatch('saveDuoApplication');
          await dispatch('saveUserPortalDuoConfig');

          return { message: 'The Duo application configuration has been saved.' };
        }

        return {};
      } catch (err) {
        dispatch(`${notificationModule}/triggerErrorNotification`, {
          title: DuoMessages.SaveError,
        }, { root: true });
        throw new Error(DuoMessages.SaveError);
      }
    },
  },
};
